import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { 
  Activity, Brain, Calendar, Shield, Users, TrendingUp, 
  CheckCircle, ArrowRight, Play, Star, Zap, FileText,
  Heart, Stethoscope, BarChart3, Video, Home, MapPin, Wifi, GraduationCap,
  IdCard, Package, MessageSquare, Briefcase, Mail
} from 'lucide-react';

const stats = [
  { value: '40%', label: 'Less Documentation Time' },
  { value: '7-day', label: 'Free Forms Trial' },
  { value: 'HIPAA', label: 'SOC 2-aligned, BAA-ready' },
  { value: '24/7', label: 'Offline-first Field Mode' }
];

const features = [
  {
    icon: Brain,
    title: 'AI Clinical Intelligence',
    description: 'Voice-note summaries, dynamic form builder with PDF-to-fields AI, and care plan suggestions that cut documentation time by ~40%.'
  },
  {
    icon: Home,
    title: 'Home Care Operations',
    description: 'EVV time-clock, HHA Exchange-ready exports, aide scheduling, and an offline-first field app that syncs when you regain signal.'
  },
  {
    icon: Users,
    title: 'Patient 360 + Forms',
    description: 'A unified patient record with attachments, voice notes, signatures, and a drag-and-drop paper-layout form designer.'
  },
  {
    icon: Shield,
    title: 'HIPAA Compliance',
    description: 'PHI encryption at rest + in flight, granular audit logs, role-based access, BAA support, and Jitsi telehealth with consent capture.'
  },
  {
    icon: Briefcase,
    title: 'HR & Workforce',
    description: 'Employee onboarding wizards, auto-generated ID badges, in-service training tracking, and a kudos / employee-of-the-week leaderboard.'
  },
  {
    icon: BarChart3,
    title: 'CRM, Inbox & CSM',
    description: 'Leads with rich-text email + open/click tracking, a Platform Inbox (Resend + IMAP), and a full CSM ticket queue with Advanced Work Assignment.'
  }
];

const modules = [
  { icon: Heart, name: 'Patient 360 Profile' },
  { icon: FileText, name: 'Dynamic Forms + PDF AI' },
  { icon: Home, name: 'Home Care Ops' },
  { icon: MapPin, name: 'EVV & HHA Exchange' },
  { icon: GraduationCap, name: 'In-Service Training' },
  { icon: IdCard, name: 'HR + ID Badges' },
  { icon: Package, name: 'Asset Management' },
  { icon: Video, name: 'Telehealth (Jitsi)' },
  { icon: MessageSquare, name: 'CRM + Email Tracking' },
  { icon: Mail, name: 'Platform Inbox (IMAP)' },
  { icon: Calendar, name: 'Smart Scheduling' },
  { icon: Shield, name: 'HIPAA + Audit' }
];

const homeCareFeatures = [
  { icon: MapPin,         title: 'EVV time-clock',        desc: 'GPS-verified visit start/stop, signature capture, and one-click HHA Exchange export.' },
  { icon: Wifi,           title: 'Offline-first field app', desc: 'Aides chart in homes with zero signal — entries queue locally and sync the moment they reconnect.' },
  { icon: GraduationCap,  title: 'In-service training',   desc: 'Track required hours per aide, schedule mandatory topics, and surface a compliance-risk alert to admins.' },
  { icon: IdCard,         title: 'Onboarding + ID badges', desc: 'Wizard-driven aide onboarding, auto-generated ID badges, and an integrated kudos leaderboard.' },
];

const accreditationPartners = [
  { name: 'NY State DOH',  sub: 'LHCSA-licensed workflow', short: 'NYSDOH' },
  { name: 'HHA Exchange',  sub: 'EVV-ready exports',       short: 'HHAX' },
  { name: 'Sandata',       sub: 'EVV-compatible',          short: 'SANDATA' },
  { name: 'HIPAA',         sub: 'PHI encrypted + BAA',     short: 'HIPAA' },
  { name: 'SOC 2',         sub: 'Controls aligned',        short: 'SOC 2' },
  { name: 'HCAOA',         sub: 'Member-ready',            short: 'HCAOA' },
  { name: 'HCA-NYS',       sub: 'NY-state member-ready',   short: 'HCA-NYS' },
  { name: 'CHAP / ACHC',   sub: 'Accreditation-ready',     short: 'CHAP' },
];

const DEFAULT_PRICING = [
  { id: 'starter', name: 'Starter', price: '$49', period: '/month', description: 'For small practices getting started', features: ['Up to 5 users', '500 patient records', 'Core EHR', 'Telehealth included', 'Standard support', 'Basic AI summarization'] },
  { id: 'pro', name: 'Pro', price: '$149', period: '/month', description: 'For growing practices', features: ['Up to 25 users', '5,000 patient records', 'AI CDS & full clinical suite', 'Reports & dashboards', 'HHA Exchange-ready exports', 'Priority support'], popular: true },
  { id: 'enterprise', name: 'Enterprise', price: '$499', period: '/month', description: 'For large healthcare organizations', features: ['Unlimited users', 'Unlimited patients', 'All features included', 'Priority support & SLA', 'Custom integrations', 'Dedicated CSM'] }
];

export default function LandingPage() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);

  // Fetch live plans from API (admin-editable). Falls back to DEFAULT_PRICING on error.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${base}/api/public/pricing`).then(r => {
      const live = (r.data?.plans || []).filter(p => p.visible !== false).map(p => ({
        id: p.plan_id,
        name: p.name,
        price: `$${Math.round(p.monthly_price)}`,
        period: '/month',
        yearly_price: p.yearly_price,
        yearly_discount_pct: p.yearly_discount_pct,
        description: p.description || `${p.name} plan`,
        features: p.features || [],
        popular: !!p.popular,
      }));
      if (live.length) setPricing(live);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navigation */}
      <nav className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#0E7490] flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Savenes
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-[#64748B] hover:text-[#0A2342] transition-colors">Features</a>
            <a href="#home-care" className="text-[#64748B] hover:text-[#0A2342] transition-colors">Home Care</a>
            <a href="#how-it-works" className="text-[#64748B] hover:text-[#0A2342] transition-colors">How It Works</a>
            <a href="#pricing" className="text-[#64748B] hover:text-[#0A2342] transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block">
              <LanguageSwitcher variant="topbar" testId="landing-language-switcher" />
            </div>
            <Link to="/login">
              <Button variant="ghost" className="text-[#0A2342]" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/request-access?type=org">
              <Button className="careiq-btn-primary" data-testid="nav-get-started-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-[#0E7490]/10 text-[#0E7490] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                AI-Powered Healthcare Intelligence
              </div>
              <h1 className="text-5xl lg:text-6xl font-semibold text-[#0A2342] leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Smarter care.<br />
                <span className="text-[#0E7490]">Healthier outcomes.</span><br />
                Every day.
              </h1>
              <p className="text-xl text-[#64748B] leading-relaxed mb-8 max-w-xl">
                Savenes runs the boring, business-critical parts of a healthcare practice — home care, telehealth, forms, training, HR, billing —
                so your team can spend less time on paperwork and more time with patients.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/request-access?type=org">
                  <Button className="careiq-btn-primary text-lg px-8 py-6 flex items-center gap-2" data-testid="hero-get-started-btn">
                    Start Free Trial
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Button variant="outline" className="careiq-btn-outline text-lg px-8 py-6 flex items-center gap-2" data-testid="hero-demo-btn">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-[#64748B]">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#166534]" />
                  14-day free trial
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#166534]" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#166534]" />
                  HIPAA compliant
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in-up animation-delay-200">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b4e0bfa7-d675-416a-864e-57470211af20/images/b01bcafb79bcec34d5048ae4cd6d093d8b0584834ccd669cd7398c76e7b1db9b.png"
                alt="Modern clinic"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0E7490]/10 rounded-full flex items-center justify-center">
                  <Home className="w-6 h-6 text-[#0E7490]" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-[#0A2342]">Licensed Home Care</div>
                  <div className="text-sm text-[#64748B]">Built for HHA agencies</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-[#0A2342]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-[#94A3B8]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditation & partner strip
          Trust signals tailored for Licensed Home Care Service Agencies
          (NYSDOH, HHA Exchange, Sandata, HIPAA, SOC 2, HCAOA, HCA-NYS,
          CHAP / ACHC). "Ready" wording is used deliberately — these
          are partners we integrate with or compliance frameworks we
          align to, not vendor endorsements. */}
      <section className="py-12 px-6 bg-white border-b border-[#E2E8F0]" data-testid="accreditation-strip">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#0E7490] font-semibold mb-1">
                Built for LHCSAs · Compliance-ready
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Aligned with the standards your auditors ask about
              </h2>
            </div>
            <p className="text-sm text-[#64748B] sm:max-w-md">
              Trusted bodies and frameworks Savenes integrates with or aligns to —
              so you go into surveys with documentation already in hand.
            </p>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {accreditationPartners.map((p, i) => (
              <li
                key={p.short}
                className="group flex flex-col items-center justify-center text-center px-3 py-4 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] hover:border-[#0E7490]/40 hover:bg-white transition-colors"
                data-testid={`accreditation-${p.short.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                title={`${p.name} — ${p.sub}`}
              >
                <div
                  className="text-[11px] font-bold tracking-wide text-[#0A2342] group-hover:text-[#0E7490]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {p.short}
                </div>
                <div className="text-[10px] text-[#64748B] mt-1 leading-tight">
                  {p.sub}
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-[#94A3B8] mt-4 text-center sm:text-left">
            Logos shown represent integration partners or compliance frameworks Savenes aligns with. Not an endorsement.
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-[#0A2342] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Healthcare runs on duct tape and 3-ring binders
            </h2>
            <p className="text-xl text-[#64748B]">
              Aides chart on paper. Clinicians juggle ten tabs. Admins chase missing in-service hours. Savenes pulls it into one quiet system that just works.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="careiq-card text-center">
              <div className="text-5xl font-bold text-[#991B1B] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>49%</div>
              <h3 className="text-xl font-semibold text-[#0A2342] mb-2">Clinician time lost</h3>
              <p className="text-[#64748B]">Nearly half of clinician time goes to administrative work instead of patient care.</p>
            </div>
            <div className="careiq-card text-center">
              <div className="text-5xl font-bold text-[#991B1B] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>3+</div>
              <h3 className="text-xl font-semibold text-[#0A2342] mb-2">Disconnected systems</h3>
              <p className="text-[#64748B]">Most agencies juggle separate tools for EVV, HR, scheduling, training, and billing. They rarely talk to each other.</p>
            </div>
            <div className="careiq-card text-center">
              <div className="text-5xl font-bold text-[#991B1B] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>40%</div>
              <h3 className="text-xl font-semibold text-[#0A2342] mb-2">Missed follow-ups</h3>
              <p className="text-[#64748B]">Untracked tasks and missed appointments compound into bad outcomes and audit headaches.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-[#0A2342] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              One platform. Every stakeholder. Real outcomes.
            </h2>
            <p className="text-xl text-[#64748B]">
              Savenes unifies clinical, operational, and financial intelligence to transform how healthcare is delivered.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="careiq-card group cursor-pointer"
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${
                  activeFeature === index ? 'bg-[#0E7490]' : 'bg-[#0E7490]/10'
                }`}>
                  <feature.icon className={`w-7 h-7 transition-colors ${
                    activeFeature === index ? 'text-white' : 'text-[#0E7490]'
                  }`} />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2342] mb-3">{feature.title}</h3>
                <p className="text-[#64748B] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Licensed Home Care Service Agency — dedicated section === */}
      <section id="home-care" className="py-24 px-6 bg-gradient-to-br from-[#F0FDFA] via-[#F8FAFC] to-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#0E7490]/10 text-[#0E7490] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Home className="w-4 h-4" />
                For Licensed Home Care Service Agencies
              </div>
              <h2 className="text-4xl lg:text-5xl font-semibold text-[#0A2342] leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Run your LHCSA on one platform —<br />
                <span className="text-[#0E7490]">paper, signals, and silos optional.</span>
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed mb-8 max-w-xl">
                Savenes is purpose-built for Licensed Home Care Service Agencies. EVV that clears HHA Exchange, an aide app that works in basements with no signal,
                in-service training that proves your hours at audit time, and an HR engine that onboards a new caregiver in minutes — not weeks.
              </p>
              <ul className="space-y-4 mb-8" data-testid="home-care-feature-list">
                {homeCareFeatures.map((f, i) => (
                  <li key={f.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#0E7490]/10 flex items-center justify-center shrink-0">
                      <f.icon className="w-5 h-5 text-[#0E7490]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#0A2342]">{f.title}</div>
                      <div className="text-sm text-[#64748B] leading-relaxed">{f.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/request-access?type=org&segment=lhcsa">
                  <Button className="careiq-btn-primary text-base px-6 py-5 flex items-center gap-2" data-testid="lhcsa-demo-btn">
                    Request a home-care demo
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button variant="outline" className="careiq-btn-outline text-base px-6 py-5" data-testid="lhcsa-pricing-btn">
                    See pricing
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] p-6 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#166534] animate-pulse" />
                    <span className="text-xs font-semibold text-[#0A2342]">Aide Field View — Offline OK</span>
                  </div>
                  <span className="text-[10px] text-[#64748B]">07:42 AM</span>
                </div>
                {[
                  { name: 'Mrs. Park (Brooklyn)', time: '08:00 – 12:00', status: 'En route', tone: '#0E7490' },
                  { name: 'Mr. Reyes (Queens)',    time: '12:30 – 14:30', status: 'Scheduled', tone: '#64748B' },
                  { name: 'Ms. Chen (Manhattan)',  time: '15:00 – 18:00', status: 'In-service due', tone: '#CA8A04' },
                ].map((v) => (
                  <div key={v.name} className="flex items-center justify-between bg-[#F8FAFC] rounded-lg p-3">
                    <div>
                      <div className="text-sm font-medium text-[#0A2342]">{v.name}</div>
                      <div className="text-xs text-[#64748B]">{v.time}</div>
                    </div>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: v.tone + '15', color: v.tone }}>{v.status}</span>
                  </div>
                ))}
                <div className="pt-3 border-t border-[#F1F5F9] flex items-center justify-between text-xs">
                  <span className="text-[#64748B] flex items-center gap-1"><Wifi className="w-3 h-3 text-[#94A3B8]" /> 0 bars — queued locally</span>
                  <span className="text-[#0E7490] font-medium">2 visits will sync</span>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white p-3 rounded-xl shadow-lg flex items-center gap-2 border border-[#E2E8F0]">
                <CheckCircle className="w-4 h-4 text-[#166534]" />
                <span className="text-xs font-medium text-[#0A2342]">HHA Exchange ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-[#0A2342] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {modules.length} Integrated Modules
            </h2>
            <p className="text-[#64748B]">Pick what you need — every module pays for itself before it pays for the next one.</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {modules.map((module, index) => (
              <div key={index} className="bg-white p-4 rounded-xl border border-[#E2E8F0] text-center hover:shadow-md transition-shadow">
                <module.icon className="w-8 h-8 text-[#0E7490] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#0A2342]">{module.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-[#0A2342] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How Savenes Works
            </h2>
            <p className="text-xl text-[#64748B]">
              Three simple steps to transform your practice with AI-powered intelligence.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="absolute top-8 left-1/2 w-full h-0.5 bg-[#E2E8F0] hidden md:block" />
              <div className="relative bg-white p-8 rounded-2xl border border-[#E2E8F0]">
                <div className="w-16 h-16 bg-[#0A2342] rounded-full flex items-center justify-center text-2xl font-bold text-white mb-6 mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  1
                </div>
                <h3 className="text-2xl font-semibold text-[#0A2342] text-center mb-4">Connect</h3>
                <p className="text-[#64748B] text-center">
                  Integrate your EHR, lab systems, pharmacy data, and billing in days—not months.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute top-8 left-1/2 w-full h-0.5 bg-[#E2E8F0] hidden md:block" />
              <div className="relative bg-white p-8 rounded-2xl border border-[#E2E8F0]">
                <div className="w-16 h-16 bg-[#0E7490] rounded-full flex items-center justify-center text-2xl font-bold text-white mb-6 mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  2
                </div>
                <h3 className="text-2xl font-semibold text-[#0A2342] text-center mb-4">Analyze</h3>
                <p className="text-[#64748B] text-center">
                  Our AI engine works 24/7 to unify records, score risks, detect care gaps, and identify revenue leakage.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="relative bg-white p-8 rounded-2xl border border-[#E2E8F0]">
                <div className="w-16 h-16 bg-[#1A56DB] rounded-full flex items-center justify-center text-2xl font-bold text-white mb-6 mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  3
                </div>
                <h3 className="text-2xl font-semibold text-[#0A2342] text-center mb-4">Act</h3>
                <p className="text-[#64748B] text-center">
                  Get role-specific insights—not data dumps. Every user sees exactly what they need to take action.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-[#0A2342] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-[#64748B]">
              Pay per active provider. Unlimited admins, billing staff, and patients included.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricing.map((plan, index) => (
              <div 
                key={index} 
                className={`relative bg-white rounded-2xl p-8 ${
                  plan.popular 
                    ? 'border-2 border-[#0E7490] shadow-xl' 
                    : 'border border-[#E2E8F0]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#0E7490] text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-semibold text-[#0A2342] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {plan.name}
                </h3>
                <p className="text-[#64748B] mb-6">{plan.description}</p>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-bold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {plan.price}
                  </span>
                  <span className="text-[#64748B]">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3 text-[#334155]">
                      <CheckCircle className="w-5 h-5 text-[#166534] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => window.location.assign(plan.price === 'Custom' ? 'mailto:sales@savenes.com?subject=Enterprise%20inquiry' : '/request-access?type=org')}
                  className={`w-full ${plan.popular ? 'careiq-btn-secondary' : 'careiq-btn-outline'}`}
                  data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                >
                  {plan.price === 'Custom' ? 'Contact Sales' : 'Start Free Trial'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 px-6 bg-[#0A2342]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
            ))}
          </div>
          <blockquote className="text-2xl md:text-3xl text-white leading-relaxed mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            "Savenes transformed how we deliver care. Our physicians spend 40% less time on documentation, 
            and our revenue cycle team caught $2.3M in previously missed charges in the first year."
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            <img 
              src="https://images.unsplash.com/photo-1615177393114-bd2917a4f74a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxkb2N0b3IlMjBwb3J0cmFpdCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzQ3MDA5NTd8MA&ixlib=rb-4.1.0&q=85&w=100"
              alt="Dr. Michael Chen"
              className="w-14 h-14 rounded-full object-cover"
            />
            <div className="text-left">
              <div className="text-white font-semibold">Dr. Michael Chen</div>
              <div className="text-[#94A3B8]">Chief Medical Officer, Valley Health Partners</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-semibold text-[#0A2342] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to transform your practice?
          </h2>
          <p className="text-xl text-[#64748B] mb-8">
            Join hundreds of healthcare organizations already using Savenes to deliver smarter care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-access?type=org">
              <Button className="careiq-btn-primary text-lg px-8 py-6" data-testid="cta-get-started-btn">
                Start Your Free Trial
              </Button>
            </Link>
            <Button variant="outline" className="careiq-btn-outline text-lg px-8 py-6" data-testid="cta-demo-btn">
              Schedule a Demo
            </Button>
          </div>
          <p className="text-sm text-[#64748B] mt-6">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2342] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Savenes
                </span>
              </div>
              <p className="text-[#94A3B8]">
                Smarter care. Healthier outcomes. Every day.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-[#94A3B8]">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-[#94A3B8]">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-[#94A3B8]">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">BAA</a></li>
                <li><a href="#" className="hover:text-white transition-colors">HIPAA Compliance</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#1E3A5F] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#94A3B8] text-sm">
              © 2024 Savenes. All rights reserved. SOC 2 Type II Certified.
            </p>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0E7490]" />
              <span className="text-sm text-[#94A3B8]">HIPAA Compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
